import React from 'react';

export interface AdminTableColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
}

interface AdminTableProps<T> {
  columns: AdminTableColumn<T>[];
  rows: T[];
  loading: boolean;
  emptyLabel?: string;
  rowKey: (row: T) => string;
  actions?: (row: T) => React.ReactNode;
}

const SKELETON_WIDTHS = ['70%', '45%', '85%', '55%', '65%'];

function AdminTable<T>({ columns, rows, loading, emptyLabel = 'Aucun élément', rowKey, actions }: AdminTableProps<T>) {
  /* Squelette plutôt que spinner : on garde l'en-tête et la grille des
     colonnes pendant le chargement, la table ne se recompose pas sous l'œil
     quand les lignes arrivent. */
  if (loading) {
    return (
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  {col.label}
                </th>
              ))}
              {actions && (
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {Array.from({ length: 6 }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col, colIndex) => (
                  <td key={col.key} className="px-4 py-2.5">
                    {/* Largeurs alternées : un damier régulier se lit comme un
                        motif décoratif, pas comme du texte en attente. */}
                    <div
                      className="h-3.5 animate-pulse rounded bg-gray-100"
                      style={{ width: SKELETON_WIDTHS[(rowIndex + colIndex) % SKELETON_WIDTHS.length] }}
                    />
                  </td>
                ))}
                {actions && (
                  <td className="px-4 py-2.5">
                    <div className="ml-auto h-3.5 w-12 animate-pulse rounded bg-gray-100" />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500 text-sm">{emptyLabel}</div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                {col.label}
              </th>
            ))}
            {actions && (
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={rowKey(row)} className="hover:bg-gray-50">
              {columns.map((col) => (
                <td key={col.key} className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">
                  {col.render ? col.render(row) : String((row as any)[col.key] ?? '—')}
                </td>
              ))}
              {actions && (
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <div className="flex justify-end gap-2">{actions(row)}</div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatusBadge({ status, tone }: { status: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }) {
  const toneClasses = {
    ok: 'bg-green-100 text-green-800',
    warn: 'bg-orange-100 text-orange-800',
    bad: 'bg-red-100 text-red-800',
    neutral: 'bg-gray-100 text-gray-700'
  }[tone];

  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${toneClasses}`}>
      {status}
    </span>
  );
}

export default AdminTable;
